/**
 * useInspectorPanelState — Track 3 Phase 7
 *
 * Owns the right-rail Inspector sidebar collapse flag extracted from
 * `App.tsx` per `docs/plans/2026-05-08_track3_app_decomposition.md`
 * row 7 ("Cleanup: ribbon / context menu / inspector / coord-system").
 *
 * Responsibilities:
 *   - Track whether the InspectorSidebar is currently collapsed.
 *   - Provide a stable `expand()` / `toggle()` pair so callers (the
 *     `openPanel` orchestrator + the InspectorSidebar header button)
 *     don't need to plumb the raw setter around.
 *
 * Returned shape:
 *   { isCollapsed, expand, toggle, setIsCollapsed }
 *
 * AutoCAD parity:
 *   AutoCAD's Properties palette and DesignCenter docking panels each
 *   carry a collapse-on-mouseout / pin-open toggle. This hook mirrors
 *   the pin-open semantics; collapse-on-mouseout is not implemented.
 */

import { useCallback, useState } from "react";

export interface UseInspectorPanelStateResult {
  isCollapsed: boolean;
  /** Force-expand (used by `openPanel` so opening a panel reveals the rail). */
  expand: () => void;
  /** Header-button toggle (matches the legacy inline `(c) => !c` setter). */
  toggle: () => void;
  /** Escape hatch for callers that need direct state access. */
  setIsCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
}

export function useInspectorPanelState(
  initiallyCollapsed = false,
): UseInspectorPanelStateResult {
  const [isCollapsed, setIsCollapsed] = useState(initiallyCollapsed);

  const expand = useCallback(() => {
    setIsCollapsed(false);
  }, []);

  const toggle = useCallback(() => {
    setIsCollapsed((current) => !current);
  }, []);

  return { isCollapsed, expand, toggle, setIsCollapsed };
}
